// These are used as feature flags
export enum OrgPermission {
  Apideck = 'apideck',
  GeminiModels = 'geminiModels',
  RelativityUsage = 'relativityUsage',
  AdminApproval = 'adminApproval',
  Require2FANonSSO = 'require2faNonSSO',
  Feedback = 'feedback',
  UsePropelAdminRoleAsDefault = 'usePropelAdminRoleAsDefault',
  ProceduralTimelines = 'proceduralTimelines',
  UseComposeNewNode = 'useComposeNewNode',
  RelativityLoadFileImport = 'relativityLoadFileImport',
  DisableZipUploadLimit = 'disableZipUploadLimit',
  GenericFileMetadata = 'genericFileMetadata',
  AlwaysUseForms = 'alwaysUseForms',
  MatterLanguage = 'matterLanguage',
  PstUploads = 'pstUploads',
}

export interface OrgPermissionConfig {
  id: string;
  permission: OrgPermission;
  title: string;
  description: string;
}

export const ORG_PERMISSION_CONFIGS: readonly OrgPermissionConfig[] = [
  {
    id: 'api-deck-toggle',
    permission: OrgPermission.Apideck,
    title: 'Apideck Integration',
    description: 'Whether the Apideck integration is enabled for this organisation.',
  },
  {
    id: 'gemini-toggle',
    permission: OrgPermission.GeminiModels,
    title: 'Gemini Models',
    description: 'Whether or not Gemini models are enabled for this organisation.',
  },
  {
    id: 'admin-approval-toggle',
    permission: OrgPermission.AdminApproval,
    title: 'Admin Approval',
    description: 'Whether the admin approval feature is enabled for this organisation.',
  },
  {
    id: 'require-2fa-toggle',
    permission: OrgPermission.Require2FANonSSO,
    title: 'Require 2FA non-SSO users',
    description:
      'Require non-SSO users to enable two-factor authentication before accessing the platform.',
  },
  {
    id: 'feedback-toggle',
    permission: OrgPermission.Feedback,
    title: 'In-app feedback',
    description: 'Whether the in-app feedback pop-ups are enabled for this organisation.',
  },
  {
    id: 'relativity-usage-toggle',
    permission: OrgPermission.RelativityUsage,
    title: 'Relativity Integration',
    description: 'Whether the Relativity integration is enabled for this organisation.',
  },
  {
    id: 'use-propel-admin-role-as-default-toggle',
    permission: OrgPermission.UsePropelAdminRoleAsDefault,
    title: 'Use Propel admin role as default',
    description:
      'When enabled, users added via PropelAuth with the Admin role are assigned the Administrator role in Wexler. When disabled, all new users get the default role.',
  },
  {
    id: 'procedural-timelines-toggle',
    permission: OrgPermission.ProceduralTimelines,
    title: 'Procedural Timelines',
    description:
      'Whether the procedural timelines feature is enabled for this organisation. Allows uploading court orders and directions to extract procedural events.',
  },
  {
    id: 'use-compose-new-node-toggle',
    permission: OrgPermission.UseComposeNewNode,
    title: 'Use Compose New Node',
    description:
      'Routes Kim agent requests for this organisation through the new compose node in chronos-ml. When disabled, the legacy compose path is used.',
  },
  {
    id: 'relativity-load-file-import-toggle',
    permission: OrgPermission.RelativityLoadFileImport,
    title: 'Relativity Load File Import',
    description:
      'Allows uploading a Relativity load file (e.g. .dat) to map and attach metadata to matching files in a matter, independently of the Relativity API integration.',
  },
  {
    id: 'disable-zip-upload-limit-toggle',
    permission: OrgPermission.DisableZipUploadLimit,
    title: 'Disable ZIP upload size limit',
    description: 'Removes the size limits on ZIP uploads for this organisation.',
  },
  {
    id: 'generic-file-metadata-toggle',
    permission: OrgPermission.GenericFileMetadata,
    title: 'Generic File Metadata',
    description:
      'Whether the generic file metadata system is enabled for this organisation. Allows attaching document metadata from load files.',
  },
  {
    id: 'always-use-forms-toggle',
    permission: OrgPermission.AlwaysUseForms,
    title: 'Always use forms',
    description: 'Whether forms are always used in pipeline extraction for this organisation.',
  },
  {
    id: 'matter-language-toggle',
    permission: OrgPermission.MatterLanguage,
    title: 'Matter Language',
    description: 'Allows users in this organisation to select the language of a matter.',
  },
  {
    id: 'pst-uploads-toggle',
    permission: OrgPermission.PstUploads,
    title: 'PST uploads',
    description:
      'Allow uploading Outlook .pst mail archives, which are unpacked server-side into individual emails and attachments.',
  },
];
